import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Layout } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const SkinSwitcherDropdown: React.FC = () => {
  const { theme, forceTheme } = useTheme();
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleThemeChange = (newTheme: 'office' | 'resto' | 'nightclub') => {
    setOpen(false);
    
    const currentLang = i18n.language || 'en';
    const skinPath = `/${currentLang}/${newTheme}`;
    
    // 🚀 OPTIMISÉ : Changement instantané sans rechargement
    forceTheme(newTheme);
    window.history.pushState({}, '', skinPath);
    
    // Déclencher manuellement popstate pour que ThemeContext se mette à jour
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        id="skin-switcher-button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-center p-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        title={t('themeSelector.title')}
        aria-label={t('themeSelector.ariaLabel')}
      >
        <Layout className="h-4 w-4 text-gray-500" />
      </button>
      
      {open && (
        <div className={`absolute ${i18n.dir(i18n.language)==='rtl' ? 'left-0' : 'right-0'} mt-1 w-48 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50`}>
          <div className="py-1">
            <button
              onClick={() => handleThemeChange('office')}
              className={`w-full ${i18n.dir(i18n.language)==='rtl' ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                theme === 'office' 
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center">
                <span className="w-4 h-4 me-2 rounded-full bg-accent1"></span>
                <span>{t('themeSelector.office.name')}</span>
                {theme === 'office' && <span className="ms-auto text-xs">✓</span>}
              </div>
            </button>
            <button
              onClick={() => handleThemeChange('resto')}
              className={`w-full ${i18n.dir(i18n.language)==='rtl' ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                theme === 'resto' 
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center">
                <span className="w-4 h-4 me-2 rounded-full bg-red-500"></span>
                <span>{t('themeSelector.resto.name')}</span>
                {theme === 'resto' && <span className="ms-auto text-xs">✓</span>}
              </div>
            </button>
            <button
              onClick={() => handleThemeChange('nightclub')}
              className={`w-full ${i18n.dir(i18n.language)==='rtl' ? 'text-right' : 'text-left'} px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 ${
                theme === 'nightclub' 
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center">
                <span className="w-4 h-4 me-2 rounded-full bg-purple-500"></span>
                <span>{t('themeSelector.nightclub.name')}</span>
                {theme === 'nightclub' && <span className="ms-auto text-xs">✓</span>}
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkinSwitcherDropdown;